import { useMemo } from 'react';
import { Button, Flex, Input, Menu, MenuContent, MenuTrigger, Text } from '@optiaxiom/react';
import { IconSearch, IconXmark } from '@optimizely/axiom-icons';

import { styles } from './ToolSearchInput.css';

type ToolSearchInputProps = {
  filterText: string;
  handleFilterChange: (value: string) => void;
  onClickAddToolRegistry: () => void;
  serviceProviderNames: string[];
  activeServiceProvider: string;
  onChangeServiceProvider: (provider: string) => void;
  selectedStatus: string;
  onChangeStatus: (status: string) => void;
  selectedEnabledInPrimaryAgent: string;
  onChangeEnabledInPrimaryAgent: (enabled: string) => void;
};

export function ToolSearchInput({
  filterText,
  handleFilterChange,
  onClickAddToolRegistry,
  serviceProviderNames,
  activeServiceProvider,
  onChangeServiceProvider,
  selectedStatus,
  onChangeStatus,
  selectedEnabledInPrimaryAgent,
  onChangeEnabledInPrimaryAgent,
}: ToolSearchInputProps) {
  const serviceProviderOptions = useMemo(
    () =>
      ['All', ...serviceProviderNames].map(name => ({
        label: name,
        execute: () => onChangeServiceProvider(name),
        selected: name === activeServiceProvider,
      })),
    [serviceProviderNames, activeServiceProvider, onChangeServiceProvider]
  );

  const statusOptions = useMemo(
    () =>
      ['All', 'Enabled', 'Disabled'].map(status => ({
        label: status,
        execute: () => onChangeStatus(status),
      })),
    [onChangeStatus]
  );

  const enabledInPrimaryAgentOptions = useMemo(
    () =>
      ['All', 'Enabled', 'Disabled'].map(option => ({
        label: option,
        execute: () => onChangeEnabledInPrimaryAgent(option),
      })),
    [onChangeEnabledInPrimaryAgent]
  );

  return (
    <Flex className={styles.searchInputContainer}>
      <Flex flexDirection="row" gap="8">
        <Input
          className={styles.searchInput}
          addonBefore={<IconSearch />}
          addonAfter={filterText && <IconXmark onClick={() => handleFilterChange('')} />}
          placeholder="Enter name or description"
          value={filterText}
          onChange={e => handleFilterChange(e.target.value)}
        />
        <Menu options={serviceProviderOptions}>
          <MenuTrigger h="40">
            <Flex gap="0">
              <Text textAlign="start" fontSize="sm">
                Provider
              </Text>
              <Text textAlign="start" fontSize="xs">
                {activeServiceProvider}
              </Text>
            </Flex>
          </MenuTrigger>
          <MenuContent />
        </Menu>
        <Menu options={statusOptions}>
          <MenuTrigger h="40">
            <Flex gap="0">
              <Text textAlign="start" fontSize="sm">
                Active Status
              </Text>
              <Text textAlign="start" fontSize="xs">
                {selectedStatus}
              </Text>
            </Flex>
          </MenuTrigger>
          <MenuContent />
        </Menu>
        <Menu options={enabledInPrimaryAgentOptions}>
          <MenuTrigger h="40">
            <Flex gap="0">
              <Text textAlign="start" fontSize="sm">
                Enabled in Chat
              </Text>
              <Text textAlign="start" fontSize="xs">
                {selectedEnabledInPrimaryAgent}
              </Text>
            </Flex>
          </MenuTrigger>
          <MenuContent />
        </Menu>
      </Flex>
      <Button appearance="primary" size="md" onClick={onClickAddToolRegistry}>
        Add tool registry
      </Button>
    </Flex>
  );
}
